import { InformationsPreAssurabilite } from "../../models";
import Loading from "./Loading";
import Assure from "./Assure";
import { Decision, Exigence } from "../../util/Constantes";
import React from "react";
import { CSSTransition } from "react-transition-group";
import i18n from "../../resources/I18n";
import "./questionnaire.css";
import { useCybermetrieAMCContext } from "../utils/CybermetrieAMCContext";
import { CybermetriesAMC } from "../../util/CybermetrieAMC";

interface IQuestionnaireProps {
  parcours: string;
  informationsPreAssurabilite: InformationsPreAssurabilite;
  enChargement: boolean;
  handleReponseQuestion: any;
  idAssure: string;
  onClickObtenirReponse: () => void;
  showVerdict: boolean;
  estNouvelleMarge: boolean;
  showBtnObtenirReponse: boolean;
}

function Questionnaire(props: Readonly<IQuestionnaireProps>) {
  const verdictTitleRef = React.useRef<HTMLHeadingElement | null>(null);
  const estEmprunteurUnique = (props.informationsPreAssurabilite.assures || []).length === 1;
  let cybermetrieParams = useCybermetrieAMCContext();
  // Trouver le nom de l'autre assuré qui prend de l'assurance
  const b2Preneur = props.informationsPreAssurabilite.assures?.find(
    (assure) => assure.id !== props.idAssure && assure.estPreneurAssurance && !assure.indicateurEmprunteurPrimaire
  );
  const questions = props.informationsPreAssurabilite.questions;
  const nodeRef = React.useRef(null);

  React.useEffect(() => {
    if (!props.showVerdict) return;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const raf = requestAnimationFrame(() => {
      const el = verdictTitleRef.current;
      if (!el || cancelled) return;

      if (typeof (el as any).scrollIntoView === "function") {
        (el as any).scrollIntoView({ behavior: "smooth", block: "start" });
      }

      timeoutId = setTimeout(() => {
        if (cancelled) return;
        const currentEl = verdictTitleRef.current;
        if (!currentEl) return;

        try {
          (currentEl as any).focus?.({ preventScroll: true });
        } catch {
          currentEl.focus?.();
        }
      }, 50);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [
    props.showVerdict,
    props.informationsPreAssurabilite?.decision,
    props.informationsPreAssurabilite?.exigence,
  ]);


  function renderB2Preneur(suffixKey: string) {
    if (!b2Preneur) return null;
    const b2PreneurNom = `${b2Preneur.prenom ?? ""} ${b2Preneur.nom ?? ""}`.trim();
    return (
      <div style={{ marginTop: "12px" }}>
        <span style={{ fontWeight: "bold", textTransform: "uppercase" }}>{b2PreneurNom}</span>
        {i18n.t(suffixKey)}
      </div>
    );
  }

  function getDecisionAccText() {
    if (estEmprunteurUnique && props.estNouvelleMarge) return i18n.t("amc:decision-acc-sb-nouvelle-marge-texte");
    if (!estEmprunteurUnique && props.estNouvelleMarge) return i18n.t("amc:decision-acc-b1-nouvelle-marge-texte");
    if (estEmprunteurUnique && !props.estNouvelleMarge) return i18n.t("amc:decision-acc-sb-changement-limite");
    return i18n.t("amc:decision-acc-b1-changement-limite");
  }

  function getTexteDebutProtection() {
    if (estEmprunteurUnique) {
        return props.estNouvelleMarge
          ? i18n.t("amc:texte-debut-protection-sb-nouvelle-marge")
          : i18n.t("amc:texte-debut-protection-sb-changement-limite");
    }
    return props.estNouvelleMarge
      ? i18n.t("amc:texte-debut-protection-b1-nouvelle-marge")
      : i18n.t("amc:texte-debut-protection-b1-changement-limite");
  } 

  function afficherVerdict() {
    const informationsPreAssurabilite = props.informationsPreAssurabilite;
    if (informationsPreAssurabilite.decision === Decision.Accepte) {
      CybermetriesAMC.EnvoieAffichageReponseAMC(cybermetrieParams, "acceptee");
      return (
        <dsd-container
          className="alerte-decision-acc-amc"
          background-color="dsd-color-background-confirmation"
          data-testid="alerte-decision-acc-amc"
        >
          <h3 ref={verdictTitleRef} tabIndex={-1} className="titre-modale-h3-acc-amc">{i18n.t("amc:decision-acc-titre")}</h3>
          <div>{getDecisionAccText()}</div>
          {renderB2Preneur("amc:suffixe-message-b2-preneur-acc")}
        </dsd-container>
      );
    }
    if (informationsPreAssurabilite.exigence === Exigence.RapportAssurabilite) {
      CybermetriesAMC.EnvoieAffichageReponseAMC(cybermetrieParams, "decision a venir");
      const texteprotectionTemporaire = props.estNouvelleMarge
        ? i18n.t("amc:protection-temporaire-nouvelle-marge-texte")
        : i18n.t("amc:protection-temporaire-changement-marge-texte");

      return (
        <dsd-container
          id="alerte-decision-ra-amc"
          background-color="dsd-color-background-decorative-petroleum-200"
          data-testid="alerte-decision-ra-amc"
        >
          <div>
            <h3 ref={verdictTitleRef} tabIndex={-1} className="titre-modale-h3-ra-amc">{i18n.t("amc:decision-a-venir")}</h3>
          </div>
          <p>{i18n.t("amc:ra-besoin-infos")}</p>
          <h4 className="sous-titre-modale-exigence-ra">{i18n.t("amc:information-importante")}</h4>
          <p>
            <span dangerouslySetInnerHTML={{ __html: i18n.t("amc:info-importante-liste-puces") }}></span>
          </p>
          <h4 className="sous-titre-modale-exigence-ra">{i18n.t("amc:protection-assurance-temporaire")}</h4>
          <p>
            <span dangerouslySetInnerHTML={{ __html: i18n.t(texteprotectionTemporaire) }}></span>
          </p>
          <h4 className="sous-titre-modale-exigence-ra">{i18n.t("amc:debut-protection-assurance")}</h4>
          <p className="texte-debut-protection">{getTexteDebutProtection()}</p>
          {renderB2Preneur("amc:suffixe-message-b2-preneur-ra")}
        </dsd-container>
      );
    }
    return null;
  }

  return (
    <CSSTransition nodeRef={nodeRef} in={!props.enChargement} timeout={100} classNames="questionnaire-transition">
      <div ref={nodeRef}>
        {props.enChargement ? (
          <Loading />
        ) : (
          <Assure
            parcours={props.parcours}
            questions={questions}
            handleReponseQuestion={props.handleReponseQuestion}
            informationsPreAssurabilite={props.informationsPreAssurabilite}
            idAssure={props.idAssure}
            onClickObtenirReponse={props.onClickObtenirReponse}
            showBtnObtenirReponse={props.showBtnObtenirReponse}
          />
        )}
        {props.showVerdict && afficherVerdict()}
      </div>
    </CSSTransition>
  );
}

export default Questionnaire;
